// src/pages/SwitchDetalle.jsx — un switch por dentro
//
// La forma está tomada del EdgeSwitch de Ubiquiti, que es la que ya se
// tiene en la cabeza: rail de secciones a la izquierda, el panel con los
// medidores, el equipo, el mapa y sus puertos, y la configuración de VLANs
// en su propia sección.
//
// Lo que cambia respecto a Ubiquiti es que esto se aplica sobre un Huawei
// por SSH, y que la app impide de entrada las combinaciones imposibles en
// vez de dejar que el equipo las acepte y se porte raro.
//
// Todo lo que se ve se lee del equipo en el momento (GET
// /switches/:id/estado). Nada se guarda en la base: una copia local se
// desincroniza en cuanto alguien entra por consola.
//
// Lo que TODAVÍA no está: aplicar cambios de VLAN. La matriz se puede
// editar y enseña los comandos exactos que se ejecutarían, pero el switch
// no se toca hasta que el backend genere esos comandos por su cuenta.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Switches.css";
import { Boton, EncabezadoPagina, Iconos, Tarjeta } from "../componentes/base";
import { authFetch } from "../utils/authFetch";
import NubeParticulas from "../componentes/espera/NubeParticulas";
import {
  UNTAGGED, TAGGED, EXCLUIDO,
  distribuirPorGrupos, gruposDePuertos, etiquetaPuerto, celda, caracterDelPuerto,
  ETIQUETA_CARACTER, negociaSolo, puertosDelPlan,
  bloqueoDuro, conflictosDeUntagged, avisosDeLaMatriz, generarComandos, normalizarNombreVlan,
} from "./Switches.logica";

/* Los cinco `display` de la lectura del switch, en su orden, para la espera. */
const LECTURAS_SWITCH = [
  { icono: Iconos.info, rotulo: "Versión" },
  { icono: Iconos.equipos, rotulo: "Puertos" },
  { icono: Iconos.senal, rotulo: "Velocidad y dúplex" },
  { icono: Iconos.panel, rotulo: "VLANs de cada puerto" },
  { icono: Iconos.planes, rotulo: "VLANs del equipo" },
];

/* ------------------------------------------------------------------ */
/* Formato                                                            */
/* ------------------------------------------------------------------ */

/** Tráfico MEDIDO por el equipo, en bits por segundo: es el promedio de
 *  los últimos 300 segundos que da `display interface`. No es un valor
 *  instantáneo, así que no se le ponen más decimales de los que aguanta. */
function formatoBps(bps) {
  if (bps === null || bps === undefined) return "—";
  if (bps <= 0) return "0";
  if (bps >= 1e9) return `${(bps / 1e9).toFixed(2)} Gbps`;
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(1)} Mbps`;
  if (bps >= 1e3) return `${Math.round(bps / 1e3)} kbps`;
  return `${bps} bps`;
}

function formatoUso(porcentaje) {
  if (porcentaje === null || porcentaje === undefined) return "—";
  return `${porcentaje.toFixed(2)}%`;
}

function formatoUptime(seg) {
  const d = Math.floor(seg / 86400);
  const h = Math.floor((seg % 86400) / 3600);
  const m = Math.floor((seg % 3600) / 60);
  return d ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
}

function formatoVelocidad(mbps) {
  if (!mbps) return "—";
  return mbps >= 1000 ? `${mbps / 1000} Gbps` : `${mbps} Mbps`;
}

/** La clase de color del puerto en el mapa, con la misma lectura que la
 *  leyenda: el color dice la velocidad, el gris dice que no hay enlace. */
function claseVelocidad(p) {
  if (p.estado === "disabled") return "sw-puerto--deshabilitado";
  if (p.estado !== "up") return "sw-puerto--sin-enlace";
  // Sin velocidad leída no se pinta un color: antes caía al naranja de
  // 100M, que es afirmar algo que no se sabe.
  if (!p.velocidadMbps) return "sw-puerto--sin-enlace";
  if (p.velocidadMbps >= 100000) return "sw-puerto--100g";
  if (p.velocidadMbps >= 25000) return "sw-puerto--25g";
  if (p.velocidadMbps >= 10000) return "sw-puerto--10g";
  if (p.velocidadMbps >= 1000) return "sw-puerto--1g";
  return "sw-puerto--100m";
}

const MODOS = [
  { valor: "auto", texto: "Autodetect" },
  { valor: "1000-full", texto: "1 Gbps FDX" },
  { valor: "100-full", texto: "100 Mbps FDX" },
  { valor: "10-full", texto: "10 Mbps FDX" },
];

/* ------------------------------------------------------------------ */
/* Mapa de puertos                                                    */
/* ------------------------------------------------------------------ */

/**
 * El chasis: un bloque por grupo de puertos (GE, 10GE, 25GE, 100GE), y
 * dentro de cada uno los impares arriba y los pares abajo, como están
 * físicamente. El grupo se rotula porque la numeración se reinicia: hay
 * un puerto 1 en cada uno.
 */
function MapaPuertos({ sw, seleccionado, onSeleccionar }) {
  const grupos = distribuirPorGrupos(sw.puertos, gruposDePuertos(sw.puertos));

  const fila = (puertos, numerosArriba) => (
    <div className="sw-mapa__fila">
      {puertos.map((p) => (
        <div key={p.id} className="sw-mapa__celda">
          {numerosArriba && <span className="sw-mapa__num">{p.n}</span>}
          <button
            type="button"
            className={`sw-puerto ${claseVelocidad(p)}${seleccionado === p.id ? " sw-puerto--activo" : ""}`}
            onClick={() => onSeleccionar(p.id)}
            title={`${p.interfaz}${p.nombre ? ` — ${p.nombre}` : ""} · ${p.estado === "up" ? formatoVelocidad(p.velocidadMbps) : "sin enlace"}`}
          >
            {p.gestion && <Iconos.ajustes size={11} strokeWidth={2.4} />}
          </button>
          {!numerosArriba && <span className="sw-mapa__num">{p.n}</span>}
        </div>
      ))}
    </div>
  );

  return (
    <div className="sw-mapa">
      <div className="sw-mapa__grupos">
        {grupos.map((g) => (
          <div key={g.clave} className="sw-mapa__grupo">
            {fila(g.arriba, true)}
            {fila(g.abajo, false)}
            <span className="sw-mapa__etiqueta">{g.clave} · {g.etiqueta}</span>
          </div>
        ))}
      </div>

      <div className="sw-leyenda">
        <span><i className="sw-punto sw-puerto--100m" />100/10 Mbps</span>
        <span><i className="sw-punto sw-puerto--1g" />1 Gbps</span>
        <span><i className="sw-punto sw-puerto--10g" />10 Gbps</span>
        <span><i className="sw-punto sw-puerto--25g" />25 Gbps</span>
        <span><i className="sw-punto sw-puerto--100g" />100 Gbps</span>
        <span><i className="sw-punto sw-puerto--deshabilitado" />Deshabilitado</span>
        <span><i className="sw-punto sw-puerto--sin-enlace" />Sin enlace</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* El chasis mientras se aplica un cambio                             */
/* ------------------------------------------------------------------ */

/**
 * El mismo chasis del panel, en chico, con los puertos que toca el cambio
 * marcados. Una lista de comandos no dice si estás tocando el puerto del
 * router o uno libre; el mapa sí, y de un vistazo.
 */
function MapaCambios({ sw, afectados, trabajando }) {
  const grupos = distribuirPorGrupos(sw.puertos, gruposDePuertos(sw.puertos));
  const marcados = new Set(afectados);

  const fila = (puertos) => (
    <div className="sw-mini__fila">
      {puertos.map((p) => (
        <span
          key={p.id}
          title={`${p.interfaz}${marcados.has(p.interfaz) ? " — se está configurando" : ""}`}
          // Solo los puertos que cambian llevan su color; el resto se
          // queda en gris para que se vea la silueta del chasis sin
          // competir con lo que importa.
          className={marcados.has(p.interfaz)
            ? `sw-mini ${claseVelocidad(p)} ${trabajando ? "sw-mini--tocando" : "sw-mini--listo"}`
            : "sw-mini"}
        />
      ))}
    </div>
  );

  return (
    <div className="sw-mini__grupos">
      {grupos.map((g) => (
        <div key={g.clave} className="sw-mini__grupo">
          {fila(g.arriba)}
          {fila(g.abajo)}
          <span className="sw-mini__etiqueta">{g.clave}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tabla de puertos                                                   */
/* ------------------------------------------------------------------ */

// La descripción sí se edita: viaja al equipo como `description` con el
// resto de los cambios. El modo se muestra tal cual porque en este switch
// solo los puertos GE tienen `speed`, y eso va en su propio paso.
function TablaPuertos({ sw, vlans, seleccionado, onSeleccionar, onRenombrar, onReset, onInfo }) {
  return (
    <div className="sw-tabla-envoltura">
      <table className="sw-tabla">
        <thead>
          <tr>
            <th className="sw-col-num">Interfaz</th>
            <th>Nombre</th>
            <th>Uso Rx</th>
            <th>Uso Tx</th>
            <th>VLANs</th>
            <th>Modo</th>
            <th className="sw-col-acciones" />
          </tr>
        </thead>
        <tbody>
          {sw.puertos.map((p) => {
            const { tipo, untagged, tagged } = caracterDelPuerto(vlans, p.id);
            return (
              <tr
                key={p.id}
                className={seleccionado === p.id ? "sw-fila--activa" : undefined}
                onClick={() => onSeleccionar(p.id)}
              >
                <td className="sw-col-num">
                  <span className={`sw-punto ${claseVelocidad(p)}`} />
                  {p.interfaz}
                </td>
                <td>
                  <input
                    className="sw-nombre"
                    value={p.nombre}
                    onChange={(e) => onRenombrar(p.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="sin descripción"
                    maxLength={80}
                  />
                </td>
                {/* Rx y Tx en bits por segundo, que es lo que mide el
                    equipo. El porcentaje del enlace va en el título, para
                    saber si esos megas son mucho o poco EN ESE puerto. */}
                <td className="sw-num" title={p.estado === "up" ? `${formatoUso(p.rxUti)} del enlace` : undefined}>
                  {p.estado === "up" ? formatoBps(p.rxBps) : ""}
                </td>
                <td className="sw-num" title={p.estado === "up" ? `${formatoUso(p.txUti)} del enlace` : undefined}>
                  {p.estado === "up" ? formatoBps(p.txBps) : ""}
                </td>
                <td>
                  <div className="sw-vlans-puerto">
                    {negociaSolo(p) ? (
                      <em className="sw-auto" title="Negociación automática de tipo de enlace: deja pasar todas las VLANs">
                        Auto · todas
                      </em>
                    ) : (
                      <>
                        <span className="sw-caracter">{ETIQUETA_CARACTER[tipo]}</span>
                        {untagged && <em className="sw-u">U {untagged.id}</em>}
                        {tagged.map((v) => <em key={v.id} className="sw-t">T {v.id}</em>)}
                      </>
                    )}
                  </div>
                </td>
                {/* El modo y su velocidad se tiñen del color de esa velocidad,
                    el mismo del mapa de arriba: verde 1G, azul 10G, y así.
                    Un puerto sin enlace se queda en gris. */}
                <td className={`sw-col-modo ${p.estado === "up" ? claseVelocidad(p) : "sw-tenue"}`}>
                  <span>{MODOS.find((m) => m.valor === p.modo)?.texto || p.modo}</span>
                  {p.estado === "up" && (
                    <span className="sw-negociado" title={p.velocidadEsNominal
                      ? "Velocidad nominal de la jaula: no se pudo leer la negociada del equipo"
                      : `Velocidad negociada del enlace${p.duplex ? ` · dúplex ${p.duplex}` : ""}`}>
                      ({formatoVelocidad(p.velocidadMbps)}{p.velocidadEsNominal ? " nominal" : ""})
                    </span>
                  )}
                </td>
                <td className="sw-col-acciones" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="sw-icono" title="Información del puerto" onClick={() => onInfo(p.id)}>
                    <Iconos.info size={16} strokeWidth={1.9} />
                  </button>
                  <button
                    type="button"
                    className="sw-icono"
                    title="Reiniciar el enlace del puerto"
                    disabled={p.estado !== "up"}
                    onClick={() => onReset(p.id)}
                  >
                    <Iconos.reiniciar size={16} strokeWidth={1.9} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Panel de información de un puerto                                  */
/* ------------------------------------------------------------------ */

function PanelPuerto({ puerto, vlans, onCerrar }) {
  const { tipo, untagged, tagged } = caracterDelPuerto(vlans, puerto.id);
  const errores = (puerto.erroresEntrada || 0) + (puerto.erroresSalida || 0);

  const dato = (etiqueta, valor) => (
    <div className="sw-dato"><span>{etiqueta}</span><strong>{valor ?? "—"}</strong></div>
  );

  return (
    <div className="sw-modal" role="dialog" onClick={onCerrar}>
      <div className="sw-modal__caja sw-modal__caja--angosta" onClick={(e) => e.stopPropagation()}>
        <div className="sw-modal__cabecera">
          <h3>{puerto.interfaz}</h3>
          <button type="button" className="sw-icono" onClick={onCerrar} title="Cerrar">
            <Iconos.cerrar size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="sw-modal__cuerpo">
          {dato("Descripción", puerto.nombre || "—")}
          {dato("Puerto", `${etiquetaPuerto(puerto)}`)}
          {/* El motivo lo da el propio equipo: un
              `DOWN(The optical power is too low)` es lo que distingue un
              cable desconectado de un módulo que no acopla. */}
          {dato("Enlace", puerto.estado === "up"
            ? `${formatoVelocidad(puerto.velocidadMbps)}${puerto.duplex ? ` · ${puerto.duplex}` : ""}`
            : (puerto.estadoTexto || "Sin enlace"))}
          {dato("Negociación", puerto.negociacion && puerto.negociacion !== "-" ? puerto.negociacion : null)}

          <hr />
          {/* Promedio de los últimos 300 s, que es la ventana del equipo. */}
          {dato("Rx (5 min)", puerto.estado === "up"
            ? `${formatoBps(puerto.rxBps)} · ${formatoUso(puerto.rxUti)} del enlace` : null)}
          {dato("Tx (5 min)", puerto.estado === "up"
            ? `${formatoBps(puerto.txBps)} · ${formatoUso(puerto.txUti)} del enlace` : null)}
          {dato("Errores", errores
            ? `${errores.toLocaleString("es-MX")} (entrada ${(puerto.erroresEntrada || 0).toLocaleString("es-MX")}, salida ${(puerto.erroresSalida || 0).toLocaleString("es-MX")})`
            : "ninguno")}

          <hr />
          <p className="sw-modal__seccion">
            VLANs del puerto — {negociaSolo(puerto) ? "sin configurar (desirable)" : ETIQUETA_CARACTER[tipo]}
          </p>
          {dato("Nativa (PVID)", untagged ? `${untagged.id} · ${untagged.nombre}` : "ninguna")}
          {dato("Etiquetadas", tagged.length ? tagged.map((v) => `${v.id} (${v.nombre})`).join(", ") : "ninguna")}

          {/* Aquí iba un bloque "Equipo conectado" (MAC, fabricante,
              puerto remoto) que nunca tuvo de dónde sacar los datos: se
              pintaban tres rayas fijas. Para llenarlo hacen falta
              `display mac-address` o LLDP, que son otra lectura. */}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Matriz de VLANs                                                    */
/* ------------------------------------------------------------------ */

const SIGUIENTE = { [EXCLUIDO]: TAGGED, [TAGGED]: UNTAGGED, [UNTAGGED]: EXCLUIDO };

function MatrizVlans({ sw, vlans, celdasRojas, onCambiar, onTodos, onRenombrar, onEliminar }) {
  const grupos = distribuirPorGrupos(sw.puertos, gruposDePuertos(sw.puertos));

  const rejilla = (contenido, conEtiqueta = false) => (
    <div className="sw-matriz__rejilla">
      {grupos.map((g) => (
        <div key={g.clave} className="sw-matriz__grupo">
          {conEtiqueta && <span className="sw-matriz__grupo-nombre">{g.clave}</span>}
          <div className="sw-matriz__fila">{g.arriba.map(contenido)}</div>
          <div className="sw-matriz__fila">{g.abajo.map(contenido)}</div>
        </div>
      ))}
    </div>
  );

  const numero = (p) => (
    <span key={p.id} className="sw-matriz__num" title={p.interfaz}>{p.n}</span>
  );

  const celdaDe = (vlan) => (p) => {
    const valor = celda(vlan, p.id);
    const roja = celdasRojas.some((c) => c.vlanId === vlan.id && c.puerto === p.id);
    return (
      <button
        key={p.id}
        type="button"
        className={`sw-celda sw-celda--${valor.toLowerCase()}${roja ? " sw-celda--bloqueada" : ""}`}
        onClick={() => onCambiar(vlan.id, p, SIGUIENTE[valor])}
        title={`${p.interfaz}${p.nombre ? ` — ${p.nombre}` : ""}`}
      >
        {valor}
      </button>
    );
  };

  return (
    <div className="sw-matriz">
      {/* Los números de puerto van una sola vez arriba, no repetidos en
          cada renglón como en EdgeSwitch: con 50 puertos y varias VLANs
          esa repetición sería toda la pantalla. */}
      <div className="sw-matriz__encabezado">
        {rejilla(numero, true)}
        <div className="sw-matriz__todos">Aplicar a todos</div>
        <div className="sw-matriz__borrar" />
      </div>

      {/* Los títulos van entre los números y las celdas: cierran el
          encabezado y abren la matriz, que es donde hacen falta. */}
      <div className="sw-matriz__titulos">
        <span className="sw-vlan-id">ID</span>
        <span className="sw-matriz__titulo-nombre">Nombre</span>
        <span className="sw-leyenda-letras">
          <span><em className="sw-u">U</em> sin etiqueta</span>
          <span><em className="sw-t">T</em> etiquetada</span>
          <span><em className="sw-e">E</em> excluida</span>
        </span>
      </div>

      {vlans.map((vlan) => (
        <div key={vlan.id} className="sw-matriz__renglon">
          <div className="sw-matriz__etiqueta">
            <span className="sw-vlan-id">{vlan.id}</span>
            <input
              className="sw-nombre"
              value={vlan.nombre}
              onChange={(e) => onRenombrar(vlan.id, e.target.value)}
              maxLength={31}
              title={`En el equipo se guarda como ${normalizarNombreVlan(vlan.nombre) || "—"}`}
            />
          </div>

          {rejilla(celdaDe(vlan))}

          <div className="sw-matriz__todos">
            {[UNTAGGED, TAGGED, EXCLUIDO].map((v) => (
              <button
                key={v}
                type="button"
                className={`sw-celda sw-celda--${v.toLowerCase()} sw-celda--accion`}
                onClick={() => onTodos(vlan.id, v)}
                title={`Poner todos los puertos en ${v}`}
              >
                {v}
              </button>
            ))}
          </div>

          <div className="sw-matriz__borrar">
            <button
              type="button"
              className="sw-icono"
              disabled={vlan.id === 1}
              title={vlan.id === 1 ? "La VLAN 1 no se puede eliminar" : `Eliminar la VLAN ${vlan.id}`}
              onClick={() => onEliminar(vlan.id)}
            >
              <Iconos.eliminar size={16} strokeWidth={1.9} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Medidores del panel                                                */
/* ------------------------------------------------------------------ */

/** Dona de una sola serie. Se dibuja a mano en SVG en vez de traer una
 *  librería: es un arco y un número al centro. */
function Dona({ porcentaje, centro, sub, titulo }) {
  const r = 52;
  const circunferencia = 2 * Math.PI * r;
  const avance = Math.max(0, Math.min(100, porcentaje)) / 100;

  return (
    <div className="sw-dona">
      <p className="sw-dona__titulo">{titulo}</p>
      <svg viewBox="0 0 140 140" className="sw-dona__svg">
        <circle className="sw-dona__pista" cx="70" cy="70" r={r} />
        <circle
          className="sw-dona__arco"
          cx="70" cy="70" r={r}
          strokeDasharray={`${circunferencia * avance} ${circunferencia}`}
        />
        <text className="sw-dona__centro" x="70" y="68">{centro}</text>
        <text className="sw-dona__sub" x="70" y="88">{sub}</text>
      </svg>
    </div>
  );
}

function Medidores({ sw }) {
  const activos = sw.puertos.filter((p) => p.estado === "up");
  const porcentajeActivos = sw.puertos.length ? (activos.length / sw.puertos.length) * 100 : 0;

  return (
    <div className="sw-medidores">
      {/* Quién es el equipo y cuánto de él está en uso, juntos: son las dos
          cosas que se miran al entrar, y separadas obligaban a bajar. */}
      <Tarjeta>
        <div className="sw-equipo">
          <div className="sw-equipo__cabecera">
            <div>
              <h2 className="sw-equipo__nombre">{sw.nombre}</h2>
              <p className="sw-equipo__uptime">{formatoUptime(sw.uptimeSegundos || 0)}</p>
            </div>
            <Dona
              titulo="Puertos activos"
              porcentaje={porcentajeActivos}
              centro={`${Math.round(porcentajeActivos)}%`}
              sub={`${activos.length} de ${sw.puertos.length}`}
            />
          </div>

          <div className="sw-equipo__datos">
            <div className="sw-dato"><span>Dirección IP</span><strong>{sw.host}</strong></div>
            <div className="sw-dato"><span>Sistema</span><strong>{sw.version || "—"}</strong></div>
            <div className="sw-dato"><span>Modelo</span><strong>{sw.modelo || "—"}</strong></div>
          </div>
        </div>
      </Tarjeta>

      <Tarjeta titulo="Uso por puerto">
        <div className={`sw-barras${sw.puertos.length > 24 ? " sw-barras--muchas" : ""}`}>
          {sw.puertos.map((p) => {
            // El uso que reporta el equipo, sin convertir: la barra llena
            // es 100% de ocupación del enlace.
            const uso = p.estado === "up" ? Math.max(p.rxUti || 0, p.txUti || 0) : 0;
            return (
              <div key={p.id} className="sw-barra"
                   title={`${p.interfaz}${p.nombre ? ` — ${p.nombre}` : ""}: `
                     + `Rx ${formatoBps(p.rxBps)} (${formatoUso(p.rxUti)}) · `
                     + `Tx ${formatoBps(p.txBps)} (${formatoUso(p.txUti)})`}>
                <span
                  className={`sw-barra__relleno ${claseVelocidad(p)}`}
                  style={{ height: `${p.estado === "up" ? Math.max(2, Math.min(100, uso)) : 0}%` }}
                />
                <span className="sw-barra__num">{p.n}</span>
              </div>
            );
          })}
        </div>
        <div className="sw-leyenda">
          <span><i className="sw-punto sw-puerto--100m" />100/10 Mbps</span>
          <span><i className="sw-punto sw-puerto--1g" />1 Gbps</span>
          <span><i className="sw-punto sw-puerto--10g" />10 Gbps</span>
          <span><i className="sw-punto sw-puerto--25g" />25 Gbps</span>
          <span><i className="sw-punto sw-puerto--100g" />100 Gbps</span>
        </div>
      </Tarjeta>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pantalla                                                           */
/* ------------------------------------------------------------------ */

// El rail de la izquierda: las secciones del equipo. Igual que en el
// EdgeSwitch, el panel es lo que se mira y la configuración es donde se
// toca — separarlas evita cambiar VLANs "de pasada" mientras se revisa
// el tráfico.
const SECCIONES = [
  { clave: "panel", titulo: "Panel", icono: Iconos.panel },
  { clave: "config", titulo: "Configuración de VLANs", icono: Iconos.ajustes },
  { clave: "equipo", titulo: "El equipo: gestión, respaldo y reinicio", icono: Iconos.empresa },
];

export default function SwitchDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [seccion, setSeccion] = useState("panel");

  // Todo lo que se ve sale del equipo, leído en el momento. Nada de esto
  // se guarda en la base: una copia local se desincroniza en cuanto
  // alguien entra por consola.
  const [sw, setSw] = useState(null);
  const [coherente, setCoherente] = useState(true);
  const [vlansGuardadas, setVlansGuardadas] = useState([]);
  const [vlans, setVlans] = useState([]);
  // Los puertos tal como se leyeron: contra esto se compara lo editado,
  // porque `sw.puertos` cambia en cuanto alguien escribe una descripción.
  const [puertosGuardados, setPuertosGuardados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);

  const [seleccionado, setSeleccionado] = useState(null);
  const [infoPuerto, setInfoPuerto] = useState(null);
  const [resetPuerto, setResetPuerto] = useState(null);
  const [reiniciando, setReiniciando] = useState(false);

  // El diff que se confirma NO es el que calcula el navegador: se pide al
  // backend, que lo arma contra el estado real leído en ese momento. Así
  // lo que se ve es lo que se va a ejecutar, aunque alguien haya entrado
  // por consola mientras la pantalla estaba abierta.
  const [plan, setPlan] = useState(null);      // { cargando, bloques, error }
  const [aplicando, setAplicando] = useState(false);
  // El cambio en curso: qué puertos toca y en qué va.
  const [envio, setEnvio] = useState(null);   // { afectados, final }

  // Los datos de gestión van aparte del estado de puertos: son tres
  // comandos más y la pantalla de puertos se refresca seguido.
  const [sistema, setSistema] = useState(null);   // { cargando, datos, error }
  const [respaldando, setRespaldando] = useState(false);
  const [confirmarReinicio, setConfirmarReinicio] = useState(false);
  const [reiniciandoEquipo, setReiniciandoEquipo] = useState(false);

  const [respaldos, setRespaldos] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [restaurar, setRestaurar] = useState(null);   // { archivo, reiniciar }
  const [restaurando, setRestaurando] = useState(false);
  const [formGestion, setFormGestion] = useState(null);   // { ip, mascara, gateway }
  const [confirmarIp, setConfirmarIp] = useState(false);
  const [cambiandoIp, setCambiandoIp] = useState(false);

  const [bloqueo, setBloqueo] = useState(null);   // { texto, marcar }
  const [mensaje, setMensaje] = useState(null);
  const [vlanNueva, setVlanNueva] = useState("");

  // El aviso de resultado se borra solo: dejarlo fijo hace que a los diez
  // minutos sigas leyendo "se aplicaron 10 comandos" sin saber si es de
  // hace un rato o de ahorita.
  useEffect(() => {
    if (!mensaje) return undefined;
    const t = setTimeout(() => setMensaje(null), 8000);
    return () => clearTimeout(t);
  }, [mensaje]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga(null);
    try {
      const res = await authFetch(`/api/switches/${id}/estado`);
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.status === "OK") {
        setSw(data.switch);
        setVlans(data.vlans || []);
        setVlansGuardadas(data.vlans || []);
        setPuertosGuardados(data.switch?.puertos || []);
        setCoherente(data.coherente !== false);
      } else {
        setErrorCarga(data.detalle || "No se pudo leer el switch.");
      }
    } catch (e) {
      setErrorCarga(`No se pudo hablar con el servidor: ${e.message}`);
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const puertos = sw?.puertos || [];

  const bloques = useMemo(
    () => generarComandos({
      original: vlansGuardadas, actual: vlans,
      puertos, puertosOriginales: puertosGuardados,
    }),
    [vlansGuardadas, vlans, puertos, puertosGuardados]
  );
  const avisos = useMemo(() => avisosDeLaMatriz(vlans, puertos), [vlans, puertos]);

  const conflictos = useMemo(() => conflictosDeUntagged(vlans, puertos), [vlans, puertos]);
  const celdasRojas = useMemo(
    () => [...conflictos.flatMap((c) => c.marcar), ...(bloqueo?.marcar || [])],
    [conflictos, bloqueo]
  );

  const rechazar = (texto, marcar) => {
    setBloqueo({ texto, marcar });
    setMensaje(null);
  };

  /* ---------- edición de la matriz ---------- */

  // La celda toma SIEMPRE el valor que se pidió, salvo bloqueo duro. Si
  // eso deja dos U en el puerto, el conflicto se pinta y "Guardar" se
  // apaga; lo que no se hace es cambiarle el valor a quien lo puso.
  const cambiarCelda = (vlanId, puerto, valor) => {
    const duro = bloqueoDuro({ puerto, vlanId, valor, vlanGestion: sw?.vlanGestion });
    if (duro) return rechazar(duro.motivo, duro.marcar);

    setBloqueo(null);
    setVlans((prev) => prev.map((v) => (
      v.id !== vlanId ? v : { ...v, puertos: { ...v.puertos, [puerto.id]: valor } }
    )));
  };

  // "Aplicar a todos" no se aplica a medias: si algún puerto tiene
  // bloqueo duro, no se mueve ninguno. Media matriz aplicada es peor que
  // nada, porque nadie sabe qué quedó dentro y qué se quedó fuera.
  const aplicarATodos = (vlanId, valor) => {
    const duros = puertos
      .map((p) => bloqueoDuro({ puerto: p, vlanId, valor, vlanGestion: sw?.vlanGestion }))
      .filter(Boolean);

    if (duros.length) {
      return rechazar(duros[0].motivo, duros.flatMap((d) => d.marcar));
    }

    setBloqueo(null);
    setVlans((prev) => prev.map((v) => {
      if (v.id !== vlanId) return v;
      const celdas = {};
      puertos.forEach((p) => { celdas[p.id] = valor; });
      return { ...v, puertos: celdas };
    }));
  };

  const crearVlan = () => {
    const nid = parseInt(vlanNueva, 10);
    if (!nid || nid < 1 || nid > 4094) {
      return setBloqueo({ texto: "El ID de VLAN va de 1 a 4094." });
    }
    if (vlans.some((v) => v.id === nid)) {
      return setBloqueo({ texto: `La VLAN ${nid} ya existe.` });
    }
    setBloqueo(null);
    setVlanNueva("");
    // Nace toda en E: existe, pero todavía no pasa por ningún puerto.
    setVlans((prev) => [...prev, { id: nid, nombre: `VLAN ${nid}`, puertos: {} }].sort((a, b) => a.id - b.id));
  };

  const eliminarVlan = (vlanId) => {
    if (sw?.vlanGestion && vlanId === sw.vlanGestion) {
      return setBloqueo({ texto: `La VLAN ${vlanId} es la de gestión del switch: no se puede eliminar desde aquí.` });
    }
    setBloqueo(null);
    setVlans((prev) => prev.filter((v) => v.id !== vlanId));
  };

  const renombrarVlan = (vlanId, nombre) =>
    setVlans((prev) => prev.map((v) => (v.id === vlanId ? { ...v, nombre } : v)));

  const cambiarPuerto = (idPuerto, campos) =>
    setSw((prev) => ({
      ...prev,
      puertos: prev.puertos.map((p) => (p.id === idPuerto ? { ...p, ...campos } : p)),
    }));

  const descartar = () => {
    setVlans(vlansGuardadas);
    setSw((prev) => ({ ...prev, puertos: puertosGuardados }));
    setBloqueo(null);
    setMensaje(null);
  };

  /* ---------- el equipo ---------- */

  const cargarSistema = useCallback(async () => {
    setSistema({ cargando: true, datos: null, error: null });
    try {
      const res = await authFetch(`/api/switches/${id}/sistema`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === "OK") {
        setSistema({ cargando: false, datos: data, error: null });
      } else {
        setSistema({ cargando: false, datos: null, error: data.detalle || "No se pudo leer el equipo." });
      }
    } catch (e) {
      setSistema({ cargando: false, datos: null, error: `No se pudo hablar con el servidor: ${e.message}` });
    }
  }, [id]);

  useEffect(() => {
    if (seccion === "equipo" && sistema === null) cargarSistema();
  }, [seccion, sistema, cargarSistema]);

  const cargarRespaldos = useCallback(async () => {
    try {
      const res = await authFetch(`/api/switches/${id}/respaldos`);
      const data = await res.json().catch(() => ({}));
      if (data.status === "OK") setRespaldos(data.respaldos || []);
    } catch { /* la lista es un extra: si falla, no estorba */ }
  }, [id]);

  useEffect(() => { if (seccion === "equipo") cargarRespaldos(); }, [seccion, cargarRespaldos]);

  // El endpoint va detrás del JWT, así que un <a href> daría 401: se baja
  // con la sesión puesta y se entrega al navegador como archivo.
  const descargarRespaldo = async (nombre) => {
    try {
      const res = await authFetch(`/api/switches/${id}/respaldos/${nombre}`);
      if (!res.ok) { setMensaje("No se pudo descargar el respaldo."); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = nombre;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMensaje(`No se pudo descargar: ${e.message}`);
    }
  };

  const borrarRespaldo = async (nombre) => {
    if (!window.confirm(`¿Borrar ${nombre}? No se puede deshacer.`)) return;
    try {
      const res = await authFetch(`/api/switches/${id}/respaldos/${nombre}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      setMensaje(data.detalle || (data.status === "OK" ? "Respaldo borrado." : "No se pudo borrar."));
      cargarRespaldos();
    } catch (e) {
      setMensaje(`No se pudo hablar con el servidor: ${e.message}`);
    }
  };

  const subirRespaldo = async (archivo) => {
    if (!archivo) return;
    setSubiendo(true);
    try {
      const cuerpo = new FormData();
      cuerpo.append("archivo", archivo);
      // Sin `Content-Type` a mano: el navegador pone el suyo con el
      // delimitador del multipart, y si se lo pisamos, el servidor no
      // sabe dónde empieza el archivo.
      const res = await authFetch(`/api/switches/${id}/respaldos`, { method: "POST", body: cuerpo });
      const data = await res.json().catch(() => ({}));
      setMensaje(data.status === "OK"
        ? `Se guardó ${data.archivo} en el servidor. Ya puedes restaurarlo desde la lista.`
        : (data.detalle || "No se pudo subir el archivo."));
      cargarRespaldos();
    } catch (e) {
      setMensaje(`No se pudo hablar con el servidor: ${e.message}`);
    } finally {
      setSubiendo(false);
    }
  };

  const restaurarAhora = async (reiniciar) => {
    setRestaurando(true);
    try {
      const res = await authFetch(`/api/switches/${id}/restaurar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ archivo: restaurar.archivo, reiniciar, confirmar: true }),
      });
      const data = await res.json().catch(() => ({}));
      setRestaurar(null);
      setMensaje(data.detalle || "Restauración enviada.");
    } catch (e) {
      setMensaje(`No se pudo hablar con el servidor: ${e.message}`);
    } finally {
      setRestaurando(false);
    }
  };

  const cambiarIpGestion = async () => {
    setCambiandoIp(true);
    try {
      const res = await authFetch(`/api/switches/${id}/gestion`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formGestion, confirmar: true }),
      });
      const data = await res.json().catch(() => ({}));
      setConfirmarIp(false);
      setMensaje(data.detalle || "Cambio de IP enviado.");
      if (data.status === "OK") {
        setFormGestion(null);
        setSistema(null);     // se relee con la dirección nueva
        cargar();
      }
    } catch (e) {
      setMensaje(`No se pudo hablar con el servidor: ${e.message}`);
    } finally {
      setCambiandoIp(false);
    }
  };

  const respaldar = async () => {
    setRespaldando(true);
    try {
      const res = await authFetch(`/api/switches/${id}/respaldo`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      setMensaje(data.status === "OK"
        ? `Respaldo guardado: ${data.archivo} (${(data.bytes / 1024).toFixed(1)} KB).`
        : (data.detalle || "No se pudo respaldar."));
      cargarRespaldos();
    } catch (e) {
      setMensaje(`No se pudo hablar con el servidor: ${e.message}`);
    } finally {
      setRespaldando(false);
    }
  };

  const reiniciarEquipo = async () => {
    setReiniciandoEquipo(true);
    try {
      const res = await authFetch(`/api/switches/${id}/reiniciar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmar: true }),
      });
      const data = await res.json().catch(() => ({}));
      setConfirmarReinicio(false);
      setMensaje(data.detalle || "Se mandó la orden de reinicio.");
    } catch (e) {
      setMensaje(`No se pudo hablar con el servidor: ${e.message}`);
    } finally {
      setReiniciandoEquipo(false);
    }
  };

  /* ---------- ver el plan y aplicarlo ---------- */

  // El diff se muestra al instante con lo que ya calculó la pantalla: no
  // hay que ir al servidor a preguntar cómo está el switch, porque acabas
  // de verlo en la matriz.
  const abrirPlan = () => setPlan({ error: null });

  const aplicarCambios = async () => {
    setEnvio({ afectados: puertosDelPlan(bloques, puertos), final: null });
    setAplicando(true);

    try {
      const res = await authFetch(`/api/switches/${id}/aplicar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          actual: { vlans: vlansGuardadas, puertos: puertosGuardados },
          deseado: { vlans, puertos },
        }),
      });
      const data = await res.json().catch(() => ({}));
      const ok = res.ok && data.status === "OK";

      setEnvio((e) => ({ ...e, final: { ok, texto: data.detalle || (ok ? "Listo." : "No se pudo aplicar.") } }));

      if (ok) {
        setTimeout(() => {
          if (data.switch) { setSw(data.switch); setPuertosGuardados(data.switch.puertos || []); }
          if (data.vlans) { setVlans(data.vlans); setVlansGuardadas(data.vlans); }
          else setVlansGuardadas(vlans);
          setPlan(null);
          setEnvio(null);
          setMensaje(data.detalle || "Cambios aplicados.");
        }, 1400);
      }
    } catch (e) {
      setEnvio((v) => ({ ...v, final: { ok: false, texto: `No se pudo hablar con el servidor: ${e.message}` } }));
    } finally {
      setAplicando(false);
    }
  };

  /* ---------- reiniciar el enlace de un puerto ---------- */

  const reiniciarEnlace = async (puerto) => {
    setReiniciando(true);
    try {
      const res = await authFetch(`/api/switches/${id}/puertos/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interfaz: puerto.interfaz }),
      });
      const data = await res.json().catch(() => ({}));
      setResetPuerto(null);
      setMensaje(data.detalle || (data.status === "OK"
        ? `Se reinició el enlace de ${puerto.interfaz}.`
        : "No se pudo reiniciar el enlace."));
      if (data.status === "OK") cargar();
    } catch (e) {
      setMensaje(`No se pudo hablar con el servidor: ${e.message}`);
    } finally {
      setReiniciando(false);
    }
  };

  /* ---------- estados de carga ---------- */

  if (cargando && !sw) {
    return (
      <div className="sw-pagina">
        <EncabezadoPagina icono={Iconos.equipos} titulo="Switch" resumen="Leyendo el equipo…" />
        <Tarjeta>
          <NubeParticulas
            titulo="Leyendo el switch"
            nota="Abriendo la sesión SSH, y en ella los puertos, sus velocidades y las VLANs."
            lecturas={LECTURAS_SWITCH}
            umbral={25}
            aviso="lo normal son unos ocho, y si pasa del minuto algo se atoró"
          />
        </Tarjeta>
      </div>
    );
  }

  if (errorCarga && !sw) {
    return (
      <div className="sw-pagina">
        <EncabezadoPagina
          icono={Iconos.equipos}
          titulo="No se pudo leer el switch"
          acciones={
            <Boton variante="secundario" icono={Iconos.atras} onClick={() => navigate("/switches")}>
              Switches
            </Boton>
          }
        />
        <p className="sw-aviso sw-aviso--bloqueo">
          <Iconos.error size={16} strokeWidth={2} />
          {errorCarga}
        </p>
        <div className="sw-acciones">
          <Boton icono={Iconos.actualizar} onClick={cargar}>Reintentar</Boton>
        </div>
      </div>
    );
  }

  const puertoSel = puertos.find((p) => p.id === infoPuerto);
  const puertoReset = puertos.find((p) => p.id === resetPuerto);

  return (
    <div className="sw-pagina">
      <EncabezadoPagina
        icono={Iconos.equipos}
        titulo={sw.nombre}
        resumen={[sw.host, sw.usuario, sw.modelo, sw.version].filter(Boolean).join(" · ")}
        acciones={
          <>
            <Boton variante="secundario" icono={Iconos.actualizar} onClick={cargar} cargando={cargando}>
              Actualizar
            </Boton>
            <Boton variante="secundario" icono={Iconos.atras} onClick={() => navigate("/switches")}>
              Switches
            </Boton>
          </>
        }
      />

      {!coherente && (
        <p className="sw-aviso sw-aviso--bloqueo">
          <Iconos.error size={16} strokeWidth={2} />
          Los comandos del switch no coinciden entre sí en la lista de puertos. Puede que el
          equipo use un formato de salida que el parser todavía no entiende: mejor no configurar
          nada hasta revisarlo.
        </p>
      )}

      <div className="sw-detalle">
        <nav className="sw-rail" aria-label="Secciones del switch">
          {SECCIONES.map((s) => (
            <button
              key={s.clave}
              type="button"
              className={`sw-rail__boton${seccion === s.clave ? " sw-rail__boton--activo" : ""}`}
              onClick={() => setSeccion(s.clave)}
              title={s.titulo}
              aria-label={s.titulo}
            >
              <s.icono size={20} strokeWidth={1.8} />
            </button>
          ))}
        </nav>

        <div className="sw-detalle__contenido">
          {seccion === "panel" && (
            <>
              <Medidores sw={sw} />

              {/* El chasis a lo ancho: los cuatro grupos en un solo renglón,
                  como está el equipo en el rack. */}
              <Tarjeta>
                <MapaPuertos sw={sw} seleccionado={seleccionado} onSeleccionar={setSeleccionado} />
              </Tarjeta>

              <Tarjeta titulo="Puertos" sinEspacio>
                <TablaPuertos
                  sw={sw}
                  vlans={vlans}
                  seleccionado={seleccionado}
                  onSeleccionar={setSeleccionado}
                  onRenombrar={(idPuerto, nombre) => cambiarPuerto(idPuerto, { nombre })}
                  onReset={setResetPuerto}
                  onInfo={setInfoPuerto}
                />
              </Tarjeta>

              {mensaje && <p className="sw-aviso sw-aviso--exito">{mensaje}</p>}
            </>
          )}

          {seccion === "config" && (
            <Tarjeta
              titulo="VLANs"
              acciones={
                <div className="sw-vlan-nueva">
                  <input
                    type="number"
                    className="sw-campo"
                    placeholder="ID de VLAN"
                    value={vlanNueva}
                    onChange={(e) => setVlanNueva(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && crearVlan()}
                    min={1}
                    max={4094}
                  />
                  <Boton variante="secundario" icono={Iconos.agregar} onClick={crearVlan}>Crear</Boton>
                </div>
              }
            >
              {bloqueo && (
                <p className="sw-aviso sw-aviso--bloqueo">
                  <Iconos.error size={16} strokeWidth={2} />
                  {bloqueo.texto}
                </p>
              )}

              {conflictos.length > 0 && (
                <div className="sw-aviso sw-aviso--bloqueo">
                  <Iconos.error size={16} strokeWidth={2} />
                  <div>
                    <p><strong>
                      {conflictos.length === 1
                        ? "Hay un puerto con dos VLANs sin etiqueta. No se puede guardar así."
                        : `Hay ${conflictos.length} puertos con dos VLANs sin etiqueta. No se puede guardar así.`}
                    </strong></p>
                    {conflictos.map((c) => <p key={c.puerto.id}>{c.motivo}</p>)}
                  </div>
                </div>
              )}

              <MatrizVlans
                sw={sw}
                vlans={vlans}
                celdasRojas={celdasRojas}
                onCambiar={cambiarCelda}
                onTodos={aplicarATodos}
                onRenombrar={renombrarVlan}
                onEliminar={eliminarVlan}
              />

              <p className="sw-pie-leyenda">
                Clic en la celda para rotar E → T → U. La <em className="sw-u">U</em> es
                la VLAN nativa del puerto y solo puede haber una; etiquetadas,
                las que hagan falta.
              </p>

              {avisos.length > 0 && (
                <div className="sw-aviso sw-aviso--suave">
                  <Iconos.duda size={16} strokeWidth={2} />
                  <div>
                    {avisos.map((a, i) => <p key={i}>{a}</p>)}
                  </div>
                </div>
              )}

              {/* El resultado de aplicar se enseña AQUÍ, que es donde está
                  quien acaba de pulsar el botón. Antes solo salía en el
                  panel y el cambio se aplicaba sin confirmación visible. */}
              {mensaje && <p className="sw-aviso sw-aviso--exito">{mensaje}</p>}

              <div className="sw-acciones">
                <Boton variante="secundario" onClick={descartar} disabled={!bloques.length}>Descartar</Boton>
                <Boton
                  icono={Iconos.confirmar}
                  onClick={abrirPlan}
                  disabled={!bloques.length || conflictos.length > 0}
                  title={conflictos.length
                    ? "Primero resuelve los puertos con dos VLANs sin etiqueta"
                    : undefined}
                >
                  Guardar cambios{bloques.length ? ` (${bloques.length - 1})` : ""}
                </Boton>
              </div>
            </Tarjeta>
          )}
          {seccion === "equipo" && (
            <>
              <Tarjeta titulo="Gestión del equipo">
                {sistema?.cargando && <p className="sw-nota sw-nota--centrada">Leyendo el equipo…</p>}

                {sistema?.error && (
                  <p className="sw-aviso sw-aviso--bloqueo">
                    <Iconos.error size={16} strokeWidth={2} />
                    {sistema.error}
                  </p>
                )}

                {sistema?.datos && (
                  <>
                    <div className="sw-equipo__datos sw-equipo__datos--tres">
                      <div className="sw-dato"><span>Nombre en el equipo</span>
                        <strong>{sistema.datos.nombre || "sin nombre"}</strong></div>
                      <div className="sw-dato"><span>Modelo</span>
                        <strong>{sw.modelo || "—"}</strong></div>
                      <div className="sw-dato"><span>Sistema</span>
                        <strong>{sw.version || "—"}</strong></div>

                      <div className="sw-dato"><span>Interfaz de gestión</span>
                        <strong>{sistema.datos.gestion?.interfaz || "—"}</strong></div>
                      <div className="sw-dato"><span>Dirección IP</span>
                        <strong>{sistema.datos.gestion
                          ? `${sistema.datos.gestion.ip}/${sistema.datos.gestion.prefijo}`
                          : "—"}</strong></div>
                      <div className="sw-dato"><span>Máscara</span>
                        <strong>{sistema.datos.gestion?.mascara || "—"}</strong></div>

                      <div className="sw-dato"><span>Puerta de enlace</span>
                        <strong>{sistema.datos.gateway || "sin ruta por omisión"}</strong></div>
                      <div className="sw-dato"><span>Tiempo encendido</span>
                        <strong>{formatoUptime(sw.uptimeSegundos || 0)}</strong></div>
                    </div>

                    {/* El texto va dentro de un <div>: `.sw-aviso` es flex y un
                        <strong> suelto se volvería un elemento flex más,
                        partiendo la frase en pedazos. */}
                    {sistema.datos.fueraDeBanda && (
                      <div className="sw-aviso sw-aviso--suave">
                        <Iconos.info size={16} strokeWidth={2} />
                        <div>
                          La administración entra por {sistema.datos.gestion.interfaz}, que es el
                          puerto de gestión <strong>fuera de banda</strong>: no pertenece a ninguna
                          VLAN de datos. Por eso mover VLANs desde esta app no puede dejarte sin
                          acceso al equipo.
                        </div>
                      </div>
                    )}

                    {!formGestion ? (
                      <div className="sw-acciones">
                        <Boton
                          variante="secundario"
                          icono={Iconos.editar}
                          onClick={() => setFormGestion({
                            ip: sistema.datos.gestion?.ip || "",
                            mascara: sistema.datos.gestion?.mascara || "",
                            gateway: sistema.datos.gateway || "",
                          })}
                        >
                          Cambiar IP de gestión
                        </Boton>
                      </div>
                    ) : (
                      <>
                        <div className="sw-formulario">
                          <label className="sw-campo-grupo">
                            <span>Dirección IP</span>
                            <input className="sw-campo sw-campo--lleno" value={formGestion.ip}
                              onChange={(e) => setFormGestion((f) => ({ ...f, ip: e.target.value }))} />
                          </label>
                          <label className="sw-campo-grupo">
                            <span>Máscara</span>
                            <input className="sw-campo sw-campo--lleno" value={formGestion.mascara}
                              onChange={(e) => setFormGestion((f) => ({ ...f, mascara: e.target.value }))} />
                          </label>
                          <label className="sw-campo-grupo">
                            <span>Puerta de enlace</span>
                            <input className="sw-campo sw-campo--lleno" value={formGestion.gateway}
                              onChange={(e) => setFormGestion((f) => ({ ...f, gateway: e.target.value }))} />
                          </label>
                        </div>

                        <div className="sw-acciones">
                          <Boton variante="secundario" onClick={() => setFormGestion(null)}>Cancelar</Boton>
                          <Boton variante="peligro" icono={Iconos.confirmar}
                                 onClick={() => setConfirmarIp(true)}>
                            Cambiar IP
                          </Boton>
                        </div>
                      </>
                    )}
                  </>
                )}
              </Tarjeta>

              <Tarjeta titulo="Respaldo y reinicio">
                <p className="sw-nota" style={{ marginTop: 0 }}>
                  La configuración completa del equipo, tal como la deja <code>display
                  current-configuration</code>. Es a lo que se vuelve si un cambio sale mal:
                  guárdala antes de tocar algo grande.
                </p>

                {mensaje && <p className="sw-aviso sw-aviso--exito">{mensaje}</p>}

                {respaldos.length > 0 && (
                  <div className="sw-tabla-envoltura">
                    <table className="sw-tabla">
                      <thead>
                        <tr><th>Archivo</th><th>Fecha</th><th>Tamaño</th><th className="sw-col-acciones" /></tr>
                      </thead>
                      <tbody>
                        {respaldos.map((r) => (
                          <tr key={r.nombre}>
                            <td className="sw-num">{r.nombre}</td>
                            <td>{r.fecha}</td>
                            <td className="sw-num">{(r.bytes / 1024).toFixed(1)} KB</td>
                            <td className="sw-col-acciones">
                              <button type="button" className="sw-icono" title="Descargar"
                                      onClick={() => descargarRespaldo(r.nombre)}>
                                <Iconos.descargar size={16} strokeWidth={1.9} />
                              </button>
                              <button type="button" className="sw-icono" title="Restaurar al switch"
                                      onClick={() => setRestaurar({ archivo: r.nombre })}>
                                <Iconos.restablecer size={16} strokeWidth={1.9} />
                              </button>
                              <button type="button" className="sw-icono" title="Borrar del servidor"
                                      onClick={() => borrarRespaldo(r.nombre)}>
                                <Iconos.eliminar size={16} strokeWidth={1.9} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="sw-acciones">
                  <label className="sw-subir">
                    <input
                      type="file"
                      accept=".cfg"
                      onChange={(e) => { subirRespaldo(e.target.files?.[0]); e.target.value = ""; }}
                      disabled={subiendo}
                    />
                    <span className="bs-boton bs-boton--secundario">
                      <Iconos.importar size={16} strokeWidth={2} />
                      {subiendo ? "Subiendo…" : "Subir un respaldo"}
                    </span>
                  </label>

                  <Boton variante="secundario" icono={Iconos.respaldos}
                         onClick={respaldar} cargando={respaldando}>
                    Respaldar configuración
                  </Boton>
                  <Boton variante="peligro" icono={Iconos.reiniciar}
                         onClick={() => setConfirmarReinicio(true)}>
                    Reiniciar el equipo
                  </Boton>
                </div>
              </Tarjeta>
            </>
          )}
        </div>
      </div>

      {restaurar && (
        <div className="sw-modal" role="dialog" onClick={() => !restaurando && setRestaurar(null)}>
          <div className="sw-modal__caja" onClick={(e) => e.stopPropagation()}>
            <div className="sw-modal__cabecera"><h3>Restaurar una configuración</h3></div>
            <div className="sw-modal__cuerpo">
              <p>Se va a subir al switch:</p>
              <pre className="sw-comandos">{restaurar.archivo}</pre>

              <p className="sw-modal__nota">
                El archivo se copia al equipo y queda marcado como la configuración con la que
                arrancará. <strong>Mientras no reinicies, el switch sigue funcionando con la
                configuración actual</strong>: la guardada solo se lee al arrancar.
              </p>
              <p className="sw-modal__nota">
                Al reiniciar, el switch vuelve a como estaba cuando se hizo ese respaldo. Todo lo
                configurado después se pierde, y el equipo deja de pasar tráfico un par de minutos.
              </p>
            </div>
            <div className="sw-modal__pie">
              <Boton variante="secundario" onClick={() => setRestaurar(null)} disabled={restaurando}>
                Cancelar
              </Boton>
              <Boton variante="secundario" icono={Iconos.descargar}
                     onClick={() => restaurarAhora(false)} cargando={restaurando}>
                Subir y dejar preparada
              </Boton>
              <Boton variante="peligro" icono={Iconos.reiniciar}
                     onClick={() => restaurarAhora(true)} cargando={restaurando}>
                Subir y reiniciar ahora
              </Boton>
            </div>
          </div>
        </div>
      )}

      {confirmarIp && formGestion && (
        <div className="sw-modal" role="dialog" onClick={() => !cambiandoIp && setConfirmarIp(false)}>
          <div className="sw-modal__caja sw-modal__caja--angosta" onClick={(e) => e.stopPropagation()}>
            <div className="sw-modal__cabecera"><h3>Cambiar la IP de gestión</h3></div>
            <div className="sw-modal__cuerpo">
              <p>
                {sistema?.datos?.gestion?.ip} → <strong>{formGestion.ip} / {formGestion.mascara}</strong>
                {formGestion.gateway ? `, salida por ${formGestion.gateway}` : ""}
              </p>
              <p className="sw-modal__nota">
                La sesión con el switch se corta en cuanto se aplica: es la dirección por la que la
                app entra. El servidor se reconecta solo en la dirección nueva para poner la ruta
                y guardar, y el registro del switch se actualiza aquí mismo.
              </p>
              <p className="sw-modal__nota">
                Si el servidor no alcanza la red nueva, el cambio se queda <strong>sin guardar</strong>:
                reiniciando el equipo vuelve a {sistema?.datos?.gestion?.ip}. Esa es tu salida si algo
                sale mal.
              </p>
            </div>
            <div className="sw-modal__pie">
              <Boton variante="secundario" onClick={() => setConfirmarIp(false)} disabled={cambiandoIp}>
                Cancelar
              </Boton>
              <Boton variante="peligro" icono={Iconos.confirmar}
                     onClick={cambiarIpGestion} cargando={cambiandoIp}>
                Cambiar ahora
              </Boton>
            </div>
          </div>
        </div>
      )}

      {confirmarReinicio && (
        <div className="sw-modal" role="dialog" onClick={() => !reiniciandoEquipo && setConfirmarReinicio(false)}>
          <div className="sw-modal__caja sw-modal__caja--angosta" onClick={(e) => e.stopPropagation()}>
            <div className="sw-modal__cabecera"><h3>Reiniciar {sw.nombre}</h3></div>
            <div className="sw-modal__cuerpo">
              <p>
                <strong>Todo lo que pase por este switch se cae</strong> mientras reinicia, un par
                de minutos. Si tiene clientes detrás, se quedan sin servicio ese rato.
              </p>
              <p className="sw-modal__nota">
                El equipo arranca con la configuración <strong>guardada</strong>. Si alguien dejó
                cambios sin guardar por consola, se pierden.
              </p>
            </div>
            <div className="sw-modal__pie">
              <Boton variante="secundario" onClick={() => setConfirmarReinicio(false)} disabled={reiniciandoEquipo}>
                Cancelar
              </Boton>
              <Boton variante="peligro" icono={Iconos.reiniciar}
                     onClick={reiniciarEquipo} cargando={reiniciandoEquipo}>
                Sí, reiniciar ahora
              </Boton>
            </div>
          </div>
        </div>
      )}

      {/* ---------- modales ---------- */}
      {puertoSel && (
        <PanelPuerto puerto={puertoSel} vlans={vlans} onCerrar={() => setInfoPuerto(null)} />
      )}

      {puertoReset && (
        <div className="sw-modal" role="dialog" onClick={() => !reiniciando && setResetPuerto(null)}>
          <div className="sw-modal__caja sw-modal__caja--angosta" onClick={(e) => e.stopPropagation()}>
            <div className="sw-modal__cabecera"><h3>Reiniciar enlace</h3></div>
            <div className="sw-modal__cuerpo">
              <p>
                {puertoReset.interfaz}{puertoReset.nombre ? ` (${puertoReset.nombre})` : ""} se apaga
                y se vuelve a encender. El enlace se cae unos segundos y con él el tráfico de quien
                esté detrás.
              </p>
              <pre className="sw-comandos">
{`interface ${puertoReset.interfaz}
 restart`}
              </pre>
            </div>
            <div className="sw-modal__pie">
              <Boton variante="secundario" onClick={() => setResetPuerto(null)} disabled={reiniciando}>
                Cancelar
              </Boton>
              <Boton
                variante="peligro"
                icono={Iconos.reiniciar}
                cargando={reiniciando}
                onClick={() => reiniciarEnlace(puertoReset)}
              >
                Reiniciar enlace
              </Boton>
            </div>
          </div>
        </div>
      )}

      {plan && (
        <div className="sw-modal" role="dialog" onClick={() => !aplicando && setPlan(null)}>
          <div className="sw-modal__caja" onClick={(e) => e.stopPropagation()}>
            <div className="sw-modal__cabecera">
              <h3>Cambios por aplicar</h3>
              {!aplicando && !envio && (
                <button type="button" className="sw-icono" onClick={() => setPlan(null)} title="Cerrar">
                  <Iconos.cerrar size={18} strokeWidth={2} />
                </button>
              )}
            </div>

            <div className="sw-modal__cuerpo">
              {envio ? (
                <>
                  <div className={`sw-envio${envio.final ? (envio.final.ok ? " sw-envio--ok" : " sw-envio--mal") : ""}`}>
                    <p className="sw-envio__titulo">
                      {!envio.final
                        ? `Configurando ${envio.afectados.length} ${envio.afectados.length === 1 ? "puerto" : "puertos"} en ${sw.nombre}`
                        : envio.final.texto}
                    </p>
                    <MapaCambios sw={sw} afectados={envio.afectados} trabajando={!envio.final} />
                    <p className="sw-envio__pie">
                      {envio.afectados.length ? envio.afectados.join(" · ") : "Sin cambios en puertos"}
                    </p>
                  </div>

                  {/* Los comandos siguen a la vista, en pequeño: el mapa
                      dice dónde y esto dice exactamente qué. */}
                  <details className="sw-detalle-comandos">
                    <summary>Ver los comandos</summary>
                    {bloques.map((b) => (
                      <div key={b.titulo} className="sw-diff">
                        <h4>{b.titulo}</h4>
                        <pre className="sw-comandos">{b.comandos.join("\n")}</pre>
                      </div>
                    ))}
                  </details>
                </>
              ) : (
                <>
                  {plan.error && (
                    <p className="sw-aviso sw-aviso--bloqueo">
                      <Iconos.error size={16} strokeWidth={2} />
                      {plan.error}
                    </p>
                  )}

                  {!plan.error && (
                    <>
                      <p className="sw-modal__nota">
                        Esto es lo que se va a ejecutar en {sw.nombre}. Los puertos que no aparecen
                        aquí no se tocan.
                      </p>
                      {bloques.map((b) => (
                        <div key={b.titulo} className="sw-diff">
                          <h4>{b.titulo}</h4>
                          <pre className="sw-comandos">{b.comandos.join("\n")}</pre>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>

            {(!envio || (envio.final && !envio.final.ok)) && (
              <div className="sw-modal__pie">
                <Boton variante="secundario" onClick={() => setPlan(null)} disabled={aplicando}>
                  Cancelar
                </Boton>
                <Boton
                  icono={Iconos.confirmar}
                  onClick={aplicarCambios}
                  cargando={aplicando}
                  disabled={!!plan.error || !bloques.length}
                >
                  Aplicar al switch
                </Boton>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
